// Seeded scatter from rules as data: one candidate per cell of each rule's grid over the bounds, built by a pure cell
// builder `scatterCell(ctx, rule, i, j)` keyed by the sub-seed `subSeed(seed, "scatter:<rule>", i, j)`. Every candidate
// draws the same six numbers whether it is kept or not ("generate, then drop"), so a cell's content never depends on
// another's, on which chunk asked first, or on other rules. Candidates are dropped by chance (per region), on paths,
// in water, near placements and on slopes too steep for the rule.
//
// Spacing (IMP-0027, 0.2.0): a rule with `spacing` also drops a candidate standing nearer than `spacing.min` to an
// item of a rule it names: another rule's kept items, or its own candidates, kept or not. Of two of its own candidates
// that close, the one with the lower mark (its seed draw; then the earlier cell) is kept and the other yields. Reading
// its own neighbours as candidates means a cell never waits on a chain of cells running back across the map (0.2.1:
// 0.2.0 read its own earlier kept items, which chained, and a far chunk built first could throw). A cell is a pure
// function of its coordinates and of the candidates within `min` of it, never of which chunk asked first. A dense rule
// thins more than a sequential pass would (a candidate can yield to one that yields in turn); give it a wider cell. A
// rule without `spacing` places exactly as before.
//
// Planted defects: `WORLD_FAULTS.mathRandomScatter` draws a candidate's position from Math.random (the C2 world-hash
// check and the cell builders' stability tests must go red); `WORLD_FAULTS.spacingIgnored` keeps every candidate a
// spacing would drop (the spacing tests must go red). World's own two sit here too, so a game flips one record:
// `groundsIgnored` (the floors read as the ground under them) and `statsFirstRegion` (the counts per region go wrong
// where regions overlap).
import type { Area } from "./chunks";
import type { Layout, Placement, ScatterRule } from "./layout";
import { cellRng } from "./seeds";
import type { Terrain } from "./terrain";

/** Planted defects the world owns, beside what they break. */
export const WORLD_FAULTS = {
  /** A scatter candidate's position comes from Math.random instead of its cell's seed. */
  mathRandomScatter: false,
  /** A rule's `spacing` is ignored: its candidates stand however near the named rules' kept items. */
  spacingIgnored: false,
  /** A rule keeping its own items apart reads its earlier neighbours' kept items, as 0.2.0 did: the chain runs back
   *  across the map, and a far chunk built first throws. */
  spacingChains: false,
  /** `World.surfaceAt` ignores the placements' ground patches: a floor reads as the ground under it. */
  groundsIgnored: false,
  /** `World.scatterStats` gives an item (and an area sample) to the first region containing it, not the topmost. */
  statsFirstRegion: false,
};

/** One scattered thing. */
export interface ScatterItem {
  rule: string;
  piece: string;
  x: number;
  /** The ground's height under it, less the rule's sink. */
  y: number;
  z: number;
  yaw: number;
  scale: number;
  /** Its own seed (a variant pick). */
  seed: number;
}

/** What a cell builder reads: the world's seed, layout, terrain and placements (with their clearances). */
export interface ScatterContext {
  seed: number;
  layout: Layout;
  terrain: Terrain;
  /** Placements with the radius kept clear round each. */
  placements: readonly { p: Placement; clear: number }[];
  /** Every rule by id: what a rule's `spacing` looks its named rules up in (needed only when a rule has `spacing`). */
  rules?: ReadonlyMap<string, ScatterRule>;
  /** A cache of cells already built for a spacing's neighbours (`<rule>:<i>:<j>`), filled as they are built. A cache
   *  only: a cell is the same with or without it. */
  memo?: Map<string, ScatterItem | null>;
}

/** The candidate's position in its cell: the first two of its six draws (the same numbers `scatterCell` draws). */
function positionOf(ctx: ScatterContext, rule: ScatterRule, i: number, j: number): [number, number] {
  const rng = cellRng(ctx.seed, `scatter:${rule.id}`, i, j);
  const u1 = rng.next();
  const u2 = rng.next();
  const b = ctx.layout.bounds;
  return [b.x0 + (i + u1) * rule.cell, b.z0 + (j + u2) * rule.cell];
}

/** The candidate of cell (i, j) of `rule`'s grid by its own tests (chance, paths, water, placements, slope). */
function candidate(ctx: ScatterContext, rule: ScatterRule, i: number, j: number): ScatterItem | null {
  const rng = cellRng(ctx.seed, `scatter:${rule.id}`, i, j);
  // six draws, always, in this order
  const u1 = rng.next();
  const u2 = rng.next();
  const keep = rng.next();
  const yaw = rng.next() * Math.PI * 2;
  const s = rng.next();
  const seed = rng.u32();
  // the planted defect sits here, beside the draw it breaks
  const jx = WORLD_FAULTS.mathRandomScatter ? Math.random() : u1;
  const b = ctx.layout.bounds;
  const x = b.x0 + (i + jx) * rule.cell;
  const z = b.z0 + (j + u2) * rule.cell;
  if (x < b.x0 || x >= b.x1 || z < b.z0 || z >= b.z1) return null;
  const t = ctx.terrain;
  const region = t.regionAt(x, z);
  const chance = (region !== null ? rule.chance[region] : undefined) ?? rule.chance["*"] ?? 0;
  if (!(keep < chance)) return null;
  const clear = rule.clear ?? {};
  const path = t.pathAt(x, z);
  if (path && path.d < (clear.path ?? 1)) return null;
  const water = t.waterAt(x, z);
  if (water && water.d < (clear.water ?? 0.5)) return null;
  for (const { p, clear: c } of ctx.placements) if (Math.hypot(x - p.pos[0], z - p.pos[1]) < c + (clear.placement ?? 0)) return null;
  if (t.slopeAt(x, z) > (rule.maxSlope ?? 35)) return null;
  const scale = rule.scale[0] + (rule.scale[1] - rule.scale[0]) * s;
  return { rule: rule.id, piece: rule.piece, x, y: t.heightAt(x, z) - (rule.sink ?? 0) * scale, z, yaw, scale, seed };
}

/** Whether a kept item of a rule `rule`'s spacing names stands nearer than its `min` to `it` (cell (i, j)). */
function crowded(ctx: ScatterContext, rule: ScatterRule, i: number, j: number, it: ScatterItem, depth: number): boolean {
  const sp = rule.spacing!;
  const b = ctx.layout.bounds;
  for (const id of sp.from) {
    const other = id === rule.id ? rule : ctx.rules?.get(id);
    if (!other) throw new Error(`world: scatter rule "${rule.id}" keeps its spacing from "${id}", which isn't a rule here`);
    const i0 = Math.floor((it.x - sp.min - b.x0) / other.cell);
    const i1 = Math.floor((it.x + sp.min - b.x0) / other.cell);
    const j0 = Math.floor((it.z - sp.min - b.z0) / other.cell);
    const j1 = Math.floor((it.z + sp.min - b.z0) / other.cell);
    for (let jj = j0; jj <= j1; jj++)
      for (let ii = i0; ii <= i1; ii++) {
        if (other === rule && ii === i && jj === j) continue;
        const [px, pz] = positionOf(ctx, other, ii, jj);
        if (Math.hypot(px - it.x, pz - it.z) >= sp.min) continue;
        if (other === rule && !WORLD_FAULTS.spacingChains) {
          // its own neighbours as candidates, kept or not, so nothing chains: the lower mark, then the earlier cell, stays
          const n = candidate(ctx, rule, ii, jj);
          if (n && Math.hypot(n.x - it.x, n.z - it.z) < sp.min && (n.seed < it.seed || (n.seed === it.seed && (jj < j || (jj === j && ii < i))))) return true;
          continue;
        }
        // another rule's kept items (or, with the planted chain, its own earlier kept items as 0.2.0 read them)
        if (other === rule && (jj > j || (jj === j && ii > i))) continue;
        const n = cell(ctx, other, ii, jj, depth + 1);
        if (n && Math.hypot(n.x - it.x, n.z - it.z) < sp.min) return true;
      }
  }
  return false;
}

/** How deep a chain of spacings may run before it is called a cycle (rules naming each other). */
const MAX_DEPTH = 64;

function cell(ctx: ScatterContext, rule: ScatterRule, i: number, j: number, depth: number): ScatterItem | null {
  if (!rule.spacing || WORLD_FAULTS.spacingIgnored) return candidate(ctx, rule, i, j);
  if (depth > MAX_DEPTH) throw new Error(`world: scatter rule "${rule.id}"'s spacing runs ${MAX_DEPTH} rules deep (rules naming each other?)`);
  const key = `${rule.id}:${i}:${j}`;
  const hit = ctx.memo?.get(key);
  if (hit !== undefined) return hit;
  const it = candidate(ctx, rule, i, j);
  const out = it && crowded(ctx, rule, i, j, it, depth) ? null : it;
  ctx.memo?.set(key, out);
  return out;
}

/** The candidate of cell (i, j) of `rule`'s grid, kept or not. Pure: a function of the context and (i, j) alone. */
export function scatterCell(ctx: ScatterContext, rule: ScatterRule, i: number, j: number): ScatterItem | null {
  return cell(ctx, rule, i, j, 0);
}

/** Every rule's items whose position lies in `area` (x0 ≤ x < x1, z0 ≤ z < z1), rule by rule, cell by cell (row by
 *  row): built from the cells overlapping the area, each a pure function of its coordinates. */
export function scatterArea(ctx: ScatterContext, rules: readonly ScatterRule[], area: Area): ScatterItem[] {
  const out: ScatterItem[] = [];
  const b = ctx.layout.bounds;
  for (const rule of rules) {
    const i0 = Math.floor((area.x0 - b.x0) / rule.cell);
    const i1 = Math.ceil((area.x1 - b.x0) / rule.cell) - 1;
    const j0 = Math.floor((area.z0 - b.z0) / rule.cell);
    const j1 = Math.ceil((area.z1 - b.z0) / rule.cell) - 1;
    for (let j = j0; j <= j1; j++)
      for (let i = i0; i <= i1; i++) {
        const it = scatterCell(ctx, rule, i, j);
        if (it && it.x >= area.x0 && it.x < area.x1 && it.z >= area.z0 && it.z < area.z1) out.push(it);
      }
  }
  return out;
}
